// Typed client for the BFF: a thin fetch wrapper over the /api/v1/* route
// handlers that respects the JWT in localStorage.

// Relative paths — Next.js route handlers under /api/v1/* live in the same
// process as the SPA. Set NEXT_PUBLIC_BFF_URL only when proxying to a separate
// BFF host (e.g. dev pointed at a remote staging panel).
const BASE_URL = process.env.NEXT_PUBLIC_BFF_URL ?? "";

type ApiInit = Omit<RequestInit, "body"> & {
  body?: unknown;
};

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const token =
    typeof window === "undefined" ? null : localStorage.getItem("access_token");

  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    credentials: "include",
  });

  if (!res.ok) {
    const rawDetail = await res.text().catch(() => "");
    const contentType = res.headers.get("content-type")?.toLowerCase() ?? "";
    const looksLikeHtml =
      contentType.includes("text/html") ||
      /^\s*(?:<!doctype\s+html|<html[\s>])/i.test(rawDetail);
    const detail = looksLikeHtml
      ? JSON.stringify({
          detail: `The server returned an unexpected response (${res.status}). Please try again.`,
        })
      : rawDetail.trim() ||
        JSON.stringify({
          detail: res.statusText || `Request failed (${res.status}).`,
        });

    // Expired / invalidated session: the request carried a token but the
    // server rejected it (401). Clear the dead credentials and bounce to
    // /sign-in instead of surfacing a raw "Invalid token" error on the page.
    // Guarded so it never fires for the auth endpoints themselves (a bad
    // login is a normal 401 the form handles) or on the login route.
    if (
      res.status === 401 &&
      token &&
      typeof window !== "undefined" &&
      !path.startsWith("/api/v1/auth/") &&
      !window.location.pathname.startsWith("/sign-in")
    ) {
      try {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
      } catch {
        // ignore storage errors (private mode / quota)
      }
      const next = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.replace(`/sign-in?next=${next}&expired=1`);
    }

    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`API ${status}: ${detail}`);
  }
}

/**
 * Human-readable message for an API error. The BFF returns errors as
 * `{"detail": "<message>"}`; this unwraps that shape so pages show the plain
 * message ("Invalid token") instead of the raw JSON body. Falls back to the
 * raw detail, then to `fallback`, for non-JSON / non-ApiError cases.
 */
export function apiErrorMessage(
  err: unknown,
  fallback = "Something went wrong.",
): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.detail) as { detail?: unknown };
      if (parsed && typeof parsed.detail === "string" && parsed.detail.trim()) {
        return parsed.detail;
      }
    } catch {
      // detail wasn't JSON — fall through to the raw string
    }
    const detail = err.detail?.trim();
    if (
      detail &&
      !/^\s*(?:<!doctype\s+html|<html[\s>])/i.test(detail)
    ) {
      return detail;
    }
    return fallback;
  }
  return fallback;
}
